export enum Entities {
  buildings = 'list of building',
  building_manager = 'building manager',
  core_manager = 'core manager',
  distro_manager = 'distro manager',
  distro_dhcp_manager = 'distro(DHCP) manager',
  isp_manager = 'ISP manager',
  access_manager = 'access switch manager',
  user_management = 'user list',
  action_analytics = 'action analytics',
  action_analytics_breakdown = 'action analytics by role and user',
  action_logs = 'user action logs',
}

export enum VendorType {
  juniper = 'juniper',
  cisco = 'cisco',
  brocade = 'brocade',
  ruckus = 'ruckus',
  meraki = 'meraki',
}

enum DhcpAction {
  Created = 'created',
  Updated = 'updated',
}

export type ValidationGuideType = {
  vendor: string;
  prefixes: string[];
  vlan: string;
  entity?: string;
};

export type UserListDataType = {
  [email: string]: string[];
};

export type AccessSwitchRequestType = {
  ip_address: string | undefined;
  device_type: string | null;
  region: string | null;
  building_code: string | null;
  unit: string | undefined;
  unit_name: string | null;
  api_mode: boolean | null;
};

export type PortDataType = {
  port: string;
  status: string;
  vlan: string;
  description: string;
};

export type PerformPortBounceRequestType = AccessSwitchRequestType & {
  ports: PortDataType[];
};

export type MerakiPortDataType = {
  port_id: string;
  port: string;
  status: string;
  vlan: string;
  switch_serial: string;
  switch_name: string;
  switch_mac_address: string;
  description: string;
};

export type PerformMerakiPortBounceRequestType = AccessSwitchRequestType & {
  ports: MerakiPortDataType[];
};

export type DialogState = 'default' | 'loading' | 'success' | 'error';

export type DhcpCreateDialogState =
  | DialogState
  | 'conflict_detected'
  | 'resolution_pending'
  | 'resolution_partial'
  | 'resolution_success'
  | 'resolution_failure';

export type PortActionDialogState = DialogState | 'partial';

export type PortFlipData = {
  port: string;
  status: string;
  vlan: string;
  new_vlan: string;
  description: string;
  new_description: string;
};

export type JuniperPortFlipData = {
  port: string;
  vlan: string;
  profile: string;
  new_profile: string;
  description: string;
  new_description: string;
};

export type MerakiPortFlipData = MerakiPortDataType & {
  new_vlan: string;
  new_description: string;
};

export type PerformPortFlipRequestType = AccessSwitchRequestType & {
  ports: PortFlipData[] | JuniperPortFlipData[];
};

export type PerformMerakiPortFlipRequestType = AccessSwitchRequestType & {
  ports: MerakiPortFlipData[];
};

type DhcpReservationMetaDataType = {
  device_ip: string | undefined;
  device_vendor: string | null;
  region: string | null;
  building_code: string | null;
  unit: string;
  unit_name: string | null;
};

type DhcpReservationBaseDataType = {
  interface: string;
  dhcp_reservation_ip: string;
  dhcp_reservation_mac: string;
  dhcp_reservation_description: string;
};

type DhcpReservationExtendedDataType = DhcpReservationBaseDataType & {
  new_dhcp_reservation_description: string;
};

export type ConflictedDhcpReservationDataType = {
  ip: string;
  host: string;
  mac: string;
};

export type BaseDhcpReservationCreateRequestType = DhcpReservationMetaDataType & {
  reservation_data: DhcpReservationExtendedDataType;
};

export type DhcpReservationConflictResolutionRequestType = DhcpReservationMetaDataType & {
  reservation_data: DhcpReservationExtendedDataType;
  conflicted_reservation: ConflictedDhcpReservationDataType;
};

export type DhcpReservationDeletionRequestType = DhcpReservationMetaDataType & {
  reservation_data: DhcpReservationBaseDataType;
};

export type BulkDhcpReservationEntryType = DhcpReservationExtendedDataType;

export type BulkDhcpReservationCreateRequestType = DhcpReservationMetaDataType & {
  reservation_data: BulkDhcpReservationEntryType[];
};

export type BulkDhcpReservationDialogState = DialogState;

export type DhcpReservationDataType = {
  interface: string;
  ip: string;
  mac: string;
  description: string;
};

export type DhcpReservationEntity = {
  ip: string;
  mac: string;
  interface: string;
  host: string;
};

export type DhcpReservationCreationResponse = {
  status: DhcpAction.Created;
  data: DhcpReservationEntity;
};

export type DhcpReservationUpdateResponse = {
  status: DhcpAction.Updated;
  data: DhcpReservationEntity;
};

export type DhcpBatchReservationsResponse = {
  status: DhcpAction.Created;
  data: DhcpReservationEntity[];
};

export type SuccessfulDhcpResolution = {
  status: DhcpAction;
  data: DhcpReservationEntity;
};

export type FailedDhcpResolution = {
  status: DhcpAction;
  error: string;
};

export type DhcpReservationConflictResolutionResponse = {
  succeeded: SuccessfulDhcpResolution[];
  failed: FailedDhcpResolution[];
};

export type PortActionSuccess = {
  serial: string;
  ports: string[] | number;
};

export type PortActionFailure = PortActionSuccess & {
  error: string;
  status_code: number;
};

export type PortActionBatchResponse = {
  succeeded: PortActionSuccess[];
  failed: PortActionFailure[];
};

export type ActionLockEvent = {
  id: number;
  user_email: string;
  unit_name: string;
  interfaces: string;
  action: string;
  ttl_seconds: number;
  locked_at: string;
};

export type ApiErrorResponse = {
  detail: string;
};

export type DataSourceType = 'api' | 'cli';

export type Order = 'asc' | 'desc';

/** Result of a Mac Mini Flip/Bounce eligibility check. */
export type MacMiniValidationResult = {
  ok: boolean;
  reason?: string;
};
